import type { PanelFilters } from '@content-search/domain';
import { validateDateRange, getDefaultDateRange, getMinStartDateTime, getMaxEndDateTime } from '@content-search/utils';
import { useMemo, useCallback, useState } from 'react';

import * as styles from './FilterPanel.css';

interface DateRangeFilterProps {
  panelFilters: PanelFilters;
  updateFilters: (updates: Partial<PanelFilters>) => void;
  isLoading: boolean;
  isOpen: boolean;
}

export function DateRangeFilter({ panelFilters, updateFilters, isLoading, isOpen }: DateRangeFilterProps) {
  const [dateError, setDateError] = useState('');

  const displayDates = useMemo(() => {
    const defaultDateRange = isOpen ? getDefaultDateRange() : { startDate: '', endDate: '' };

    return {
      startDate: panelFilters.startDate || (isOpen ? defaultDateRange.startDate : ''),
      endDate: panelFilters.endDate || (isOpen ? defaultDateRange.endDate : ''),
    };
  }, [panelFilters.startDate, panelFilters.endDate, isOpen]);

  // Calculate datetime picker constraints
  const minStartDateTime = useMemo(() => getMinStartDateTime(), []);
  const maxEndDateTime = useMemo(() => getMaxEndDateTime(), []);

  const handleStartDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      updateFilters({ startDate: value });

      // Validate date range on every change
      const error = validateDateRange(value, panelFilters.endDate || displayDates.endDate);

      setDateError(error);
    },
    [updateFilters, panelFilters.endDate, displayDates.endDate]
  );

  const handleEndDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      updateFilters({ endDate: value });

      // Validate date range on every change
      const error = validateDateRange(panelFilters.startDate || displayDates.startDate, value);

      setDateError(error);
    },
    [updateFilters, panelFilters.startDate, displayDates.startDate]
  );

  return (
    <>
      <div className={styles.filterSection}>
        <label className={styles.filterLabel} htmlFor="start-date">
          Start Date (UTC)
        </label>
        <input
          className={styles.dateInput}
          disabled={isLoading}
          id="start-date"
          max={maxEndDateTime}
          min={minStartDateTime}
          placeholder="Start Date (UTC)"
          type="datetime-local"
          value={displayDates.startDate}
          onChange={handleStartDateChange}
        />
      </div>

      <div className={styles.filterSection}>
        <label className={styles.filterLabel} htmlFor="end-date">
          End Date (UTC)
        </label>
        <input
          className={styles.dateInput}
          disabled={isLoading}
          id="end-date"
          max={maxEndDateTime}
          min={minStartDateTime}
          placeholder="End Date (UTC)"
          type="datetime-local"
          value={displayDates.endDate}
          onChange={handleEndDateChange}
        />
        {dateError && <div className={styles.errorText}>{dateError}</div>}
      </div>
    </>
  );
}
